import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import { X } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import DealDetailsSlider from '../components/DealDetailsSlider';
import DealFormSlider from '../components/DealFormSlider';
import { Deal, DealFilters } from '../types/deal';
import { dealService } from '../services/dealService';
import api from '../services/api';
import '../styles/Deals.css';
import '../styles/Buttons.css';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

const Deals: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filters, setFilters] = useState<DealFilters>({});
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const isFetchingRef = useRef(false);

  // Get view state from URL params
  const viewDealId = searchParams.get('view');
  const isNewDeal = searchParams.get('action') === 'new';

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (workspaceId) {
      // Initial load
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId && selectedProjectId) {
      // Initial load when project is selected
      fetchDeals(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, selectedProjectId]);

  useEffect(() => {
    if (workspaceId && selectedProjectId && !loading && !isFetchingRef.current) {
      // Data refresh for pagination, search, filters
      fetchDeals(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, debouncedSearchQuery, filters]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project
      if (projectsList.length > 0 && !selectedProjectId) {
        setSelectedProjectId(projectsList[0]._id);
      }
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects');
      setLoading(false);
    }
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    setPagination({ ...pagination, page: 1 }); // Reset to page 1 when changing project
  };

  const fetchDeals = async (isDataRefresh: boolean = false) => {
    if (!selectedProjectId) return;

    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;

      // Use dataLoading for data refreshes (pagination, search, filters), loading for initial load
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = await dealService.getAll(
        workspaceId!,
        { ...filters, search: debouncedSearchQuery, projectId: selectedProjectId },
        pagination.page,
        pagination.limit
      );

      setDeals(response.deals);

      // Only update pagination if values actually changed to avoid triggering useEffect
      const newPagination = response.pagination;
      if (
        newPagination.page !== pagination.page ||
        newPagination.limit !== pagination.limit ||
        newPagination.total !== pagination.total ||
        newPagination.pages !== pagination.pages
      ) {
        setPagination(newPagination);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch deals');
      console.error('Error fetching deals:', err);
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
      isFetchingRef.current = false;
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleCreateDeal = () => {
    setSearchParams({ action: 'new' });
  };

  const handleDealClick = (deal: Deal) => {
    setSearchParams({ view: deal._id });
  };

  const handleCloseDetails = () => {
    setSearchParams({});
  };

  const handleDealCreated = () => {
    setSearchParams({});
    fetchDeals(); // Refresh the list
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (key: keyof DealFilters, value: any) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ ...pagination, limit, page: 1 }); // Reset to page 1 when changing limit
  };

  const getAllPurchasers = (deal: Deal): string[] => {
    if (!deal.purchasers || deal.purchasers.length === 0) {
      return ['—'];
    }
    return deal.purchasers.map(p => p.displayName || '—');
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStageColor = (stage: string): string => {
    switch (stage) {
      case 'NEW':
        return 'stage-new';
      case 'RESERVED':
        return 'stage-reserved';
      case 'FIRM':
        return 'stage-firm';
      case 'SOLD':
        return 'stage-sold';
      case 'CLOSED':
        return 'stage-closed';
      case 'CANCELLED':
        return 'stage-cancelled';
      default:
        return 'stage-default';
    }
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="deals-container">
        {/* Section Header */}
        <div className="deals-section-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 className="deals-section-title">Deals</h2>
              <p className="deals-section-subtitle">
                Manage sales agreements and transactions
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div>
                <select
                  className="form-select"
                  value={selectedProjectId}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  disabled={loading || projects.length === 0}
                  style={{
                    minWidth: '280px',
                    padding: '8px 12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: '1px solid #D4CBC4',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    color: '#48182F',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  <option value="">Select Project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
              <button className="btn btn-primary btn-md" onClick={handleCreateDeal}>
                + New Deal
              </button>
            </div>
          </div>
        </div>

        {/* White Card Container */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
        }}>
          {/* Actions Bar */}
          <div className="deals-actions">
          <div className="deals-search" style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search deals..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ paddingRight: searchQuery ? '35px' : '12px' }}
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setPagination({ ...pagination, page: 1 });
                }}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280',
                  borderRadius: '4px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.color = '#48182F';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <div className="deals-filters">
            <select
              value={filters.stage || ''}
              onChange={(e) => handleFilterChange('stage', e.target.value || undefined)}
            >
              <option value="">All Stages</option>
              <option value="NEW">New</option>
              <option value="RESERVED">Reserved</option>
              <option value="FIRM">Firm</option>
              <option value="SOLD">Sold</option>
              <option value="CLOSED">Closed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Content */}
        {error && <div className="deals-error">{error}</div>}

        {loading ? (
          <div className="builders-loading">
            <div className="builders-loading-spinner" />
          </div>
        ) : !selectedProjectId ? (
          <div className="builders-empty-state">
            <p>Please select a project to view deals</p>
          </div>
        ) : deals.length === 0 ? (
          <div className="deals-empty">
            <p>No deals found</p>
            <button className="btn btn-primary btn-md" onClick={handleCreateDeal}>
              Create your first deal
            </button>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="data-table-container" style={{ position: 'relative' }}>
              {/* Loading Overlay */}
              {dataLoading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10
                }}>
                  <div className="builders-loading-spinner"></div>
                </div>
              )}

              <table className="data-table">
                <thead>
                  <tr>
                    <th>Deal #</th>
                    <th>Stage</th>
                    <th>Purchaser</th>
                    <th>Unit</th>
                    <th>Project</th>
                    <th>Type</th>
                    <th>Total Price</th>
                    <th>Deposits Paid</th>
                    <th>Initiated</th>
                  </tr>
                </thead>
                <tbody style={{ opacity: dataLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                  {deals.map((deal, index) => (
                    <tr
                      key={deal._id}
                      onClick={() => handleDealClick(deal)}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0',
                        cursor: dataLoading ? 'wait' : 'pointer'
                      }}
                    >
                      <td>
                        <div className="deals-number">{deal.dealNumber}</div>
                      </td>
                      <td>
                        <span className={`deals-stage-badge ${getStageColor(deal.stage)}`}>
                          {deal.stage}
                        </span>
                      </td>
                      <td>
                        <div className="deals-purchaser">
                          {getAllPurchasers(deal).map((name, index) => (
                            <div key={index}>{name}</div>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="deals-unit">{deal.unitNo || '—'}</div>
                      </td>
                      <td>
                        <div className="deals-project">{deal.projectName || '—'}</div>
                      </td>
                      <td>
                        <div className="deals-type">{deal.dealType}</div>
                      </td>
                      <td>
                        <div className="deals-price">
                          {formatCurrency(deal.finance.totalPrice)}
                        </div>
                      </td>
                      <td>
                        <div className="deals-deposits">
                          {formatCurrency(deal.finance.depositsPaid)}
                        </div>
                      </td>
                      <td>
                        <div className="deals-date">{formatDate(deal.initiatedOn)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination and Records Per Page */}
            <div className="deals-pagination-controls">
              {/* Records Per Page Selector */}
              <div className="deals-records-per-page">
                <span>Show</span>
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>records per page (Total: {pagination.total})</span>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="deals-pagination-wrapper">
                  <Pagination
                    current={pagination.page}
                    total={pagination.pages}
                    onPageChange={handlePageChange}
                    maxWidth={400}
                  />
                </div>
              )}
            </div>
          </>
          )}
        </div>
      </div>

      {/* Deal Form Slider for New Deal */}
      {workspaceId && isNewDeal && (
        <DealFormSlider
          workspaceId={workspaceId}
          onSuccess={handleDealCreated}
          onCancel={handleCloseDetails}
        />
      )}

      {/* Deal Details Slider for Viewing/Editing */}
      {workspaceId && viewDealId && (
        <DealDetailsSlider
          workspaceId={workspaceId}
          dealId={viewDealId}
          isNew={false}
          onClose={handleCloseDetails}
        />
      )}
    </AppLayout>
  );
};

export default Deals;
